import FroalaEditor from 'froala-editor';

import { Reference, ReferenceWithNumber } from '@treetop/shared/model';

import { FROALA_REFERENCE_ICON } from '../../../../feature-flowchart/src/lib/infrastructure/operations/cached-icons';
import { getAllReferenceIdsFromText } from '../rich-text-editor.util';
import { isBackspaceAfterReferenceParagraph, mergeEmptyParagraphAfterReference } from './reference-backspace.util';

export const ADD_REFERENCE_BUTTON_PREFIX = 'add-reference-button';
export const ADD_REFERENCE_CONTAINER_PREFIX = 'custom-popup-container_reference';

export const addReferenceFroalaPopup = (title: string) => {
  FroalaEditor.DefineIconTemplate('addReferenceSvgTemplate', FROALA_REFERENCE_ICON);
  FroalaEditor.DefineIcon('addReferenceIcon', {
    NAME: 'add-reference-icon',
    SVG_KEY: 'add-reference-svg',
    template: 'addReferenceSvgTemplate',
  });

  FroalaEditor.RegisterCommand(ADD_REFERENCE_BUTTON_PREFIX, {
    id: ADD_REFERENCE_BUTTON_PREFIX,
    title,
    icon: 'addReferenceIcon',
    undo: false,
    focus: false,
    plugin: 'ttReferencePlugin',
    callback: function () {
      const callback = this.opts._pendingAttachReferencesPopup;
      if (!callback) {
        return;
      }

      const sel = window.getSelection();
      this.opts._pendingReferencesRange = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;

      this.ttReferencePlugin.showPopup(callback);
    },
  });
};

export const registerAddReferenceCommand = () => {
  FroalaEditor.RegisterCommand('addReference', {
    title: 'Add Reference',
    focus: true,
    undo: true,
    callback: function () {
      const references: Reference[] = this.opts._pendingReferences ?? [];
      if (!references.length) return;

      const savedRange = this.opts._pendingReferencesRange as Range | null;
      const htmlToInsert = references.map((r) => getReferenceSpan(r)).join(' ');
      if (savedRange) {
        savedRange.collapse(false);
        const fragment = savedRange.createContextualFragment(htmlToInsert);
        const lastNode = fragment.lastChild;
        savedRange.insertNode(fragment);
        if (lastNode) {
          const sel = window.getSelection();
          savedRange.setStartAfter(lastNode);
          savedRange.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(savedRange);
        }
        this.opts._pendingReferencesRange = null;
      } else {
        this.html.insert(htmlToInsert);
      }

      this.opts._pendingReferences = null;
    },
  });
};

export const createFroalaReferencesPlugin = () => {
  FroalaEditor.PLUGINS.References = function (editor) {
    const globalReferenceStore: { [id: string]: Reference } = {};

    const init = (references: Reference[] = []) => {
      console.debug('Froala References Plugin Initialized');
      for (const reference of references ?? []) {
        globalReferenceStore[reference.id] = reference;
      }

      // Use a capture-phase native listener so this runs BEFORE Froala's internal
      // keydown handlers (including fr-deletable handling and paragraph merging).
      const onKeydown = (event: KeyboardEvent) => {
        if (event.key !== 'Backspace') return;

        const selection = window.getSelection();
        if (!isBackspaceAfterReferenceParagraph(selection)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        mergeEmptyParagraphAfterReference(selection!);
        editor.undo.saveStep();
      };

      editor.el.addEventListener('keydown', onKeydown, { capture: true });

      editor.events.on('destroy', () => {
        editor.el.removeEventListener('keydown', onKeydown, { capture: true });
      });
    };

    const addReferences = (allReferences: Reference[], currentReferences: Reference[] = []): Reference[] => {
      // store pending references for Froala command
      editor.opts._pendingReferences = allReferences;
      editor.commands.exec('addReference');

      for (const ref of allReferences) {
        globalReferenceStore[ref.id] = ref;
      }

      const referenceMap = new Map<Reference['id'], Reference>();
      for (const ref of [...(currentReferences ?? []), ...allReferences]) {
        referenceMap.set(ref.id, ref);
      }

      const referenceIdsInText = getAllReferenceIdsFromText(editor.html.get());
      const orderedReferences: Reference[] = [];
      for (const id of referenceIdsInText) {
        const ref = referenceMap.get(id);
        if (ref) orderedReferences.push(ref);
      }

      return orderedReferences;
    };

    const updateReferenceNumbers = (numberedReferences: ReferenceWithNumber[]) => {
      const referenceMap = new Map(numberedReferences.map((ref) => [ref.id, ref.referenceNumber]));

      const referenceSpans = Array.from(editor.el.querySelectorAll('span[data-reference-id]')) as HTMLElement[];

      referenceSpans.forEach((span) => {
        const refId = span.getAttribute('data-reference-id');
        if (refId && referenceMap.has(refId)) {
          span.textContent = `[${referenceMap.get(refId)}]`;
        }
      });

      editor.events.trigger('contentChanged');
    };

    const syncReferences = (): Reference[] => {
      const editorContent = editor.html.get();

      return getAllReferenceIdsFromText(editorContent)
        .map((id) => globalReferenceStore[id])
        .filter(Boolean);
    };

    return { init, addReferences, updateReferenceNumbers, syncReferences };
  };
};

export const getReferenceSpan = (ref: Reference): string => {
  return `<span
    class="fr-deletable"
    data-tt-custom-element="reference"
    data-reference-id="${ref.id}"
    contenteditable="false"
  >[]</span>`;
};
